import { useState, useMemo } from 'react';
import { useGetList } from 'react-admin';
import { useMediaQuery, Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Avatar } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { SwapHoriz } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import type { TransactionResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

type TransactionStatus = 'Pending' | 'Posted' | 'Voided' | 'Reversed';

interface TransactionItem extends TransactionResponse {
  id: string;
}

const STATUS_COLORS: Record<TransactionStatus, 'warning' | 'success' | 'default' | 'error'> = {
  Pending: 'warning',
  Posted: 'success',
  Voided: 'default',
  Reversed: 'error',
};

const statusColor = (status: TransactionStatus | undefined): 'warning' | 'success' | 'default' | 'error' => {
  if (!status) return 'default';
  return STATUS_COLORS[status] ?? 'default';
};

export const TransactionList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { data: transactions, isLoading } = useGetList<TransactionItem>('transactions', {
    pagination: { page: page + 1, perPage: rowsPerPage },
  });

  const stats = useMemo(() => ({
    total: transactions?.length ?? 0,
    pending: transactions?.filter(t => t.status === 'Pending').length ?? 0,
    posted: transactions?.filter(t => t.status === 'Posted').length ?? 0,
    reversed: transactions?.filter(t => t.status === 'Reversed').length ?? 0,
    voided: transactions?.filter(t => t.status === 'Voided').length ?? 0,
  }), [transactions]);

  const typeCounts = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    transactions?.forEach(t => {
      const key = t.transactionType ?? 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [transactions]);

  const pieData: { label: string; value: number }[] = Object.entries(typeCounts).map(([label, value]) => ({ label, value: Number(value) }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="ledger" wizardPath="wizard/transaction" label="New Transaction" />
        </Box>
        <ListPageHeader
          title="Transactions"
          metrics={[
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'Posted', value: String(stats.posted), color: 'success.main' },
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {transactions?.map(t => (
            <Paper key={t.id} sx={{ p: 2 }} onClick={() => navigate(`/transactions/${t.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    <SwapHoriz sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{t.transactionNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.transactionType}</Typography>
                  </Box>
                </Box>
                <Chip label={t.status} size="small" color={statusColor(t.status)} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.transactionDate}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{t.description ?? '—'}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {t.lines?.length ?? 0} line(s)
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="ledger" wizardPath="wizard/transaction" label="New Transaction" />
      </Box>
      <ListPageHeader
        title="Transactions"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Posted', value: String(stats.posted), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Reversed', value: String(stats.reversed), color: 'error.main' },
        ]}
        pieChartData={pieData.filter(d => d.value > 0)}
        pieChartTitle="By Type"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Transaction #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Lines</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions?.map(t => (
              <TableRow
                key={t.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/transactions/${t.id}/show`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{t.transactionNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={t.transactionType} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{t.transactionDate}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{t.description ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{t.lines?.length ?? 0}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={t.status} size="small" color={statusColor(t.status)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={transactions?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!transactions || transactions.length === 0) && !isLoading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <SwapHoriz sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No transactions found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
